import {
  ArrowNarrowDownIcon,
  ArrowNarrowUpIcon,
  SwitchVertical02Icon,
} from '@langchain/untitled-ui-icons';

import { ReactNode } from 'react';

import { FEEDBACK_COLUMNS_DEFAULT_WIDTH } from '@/Pages/DatasetSessionCompare/constants';
import { usePlaygroundStore } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { IconButton, Tooltip } from '@/design-system';
import { ExperimentViewOverridesSchema, RunFacetField } from '@/types/schema';
import { capitalize } from '@/utils/stringUtils';

import { DatasetSessionCompareFilter } from '../../DatasetSessionCompareFilter';
import { AggregatedStatHeader } from '../../ExperimentsGroupByTable/AggregatedStatHeader';
import { ExperimentColumnHeader } from '../ExperimentColumnHeader';
import { useFeedbackColumnSorting } from './hooks/feedback-column-sorting';

export const FeedbackColumnHeader = ({
  feedbackKey,
  columnIndex,
  tableHeight,
  columnWidthStorage,
  setColumnWidthStorage,
  className,
  isTable = true,
  canSort,
  onChange,
  runFacets,
  currentFilters,
  sessionId,
  evaluatorFeedbackKeys,
  columnOverride,
  statDisplay,
  canFilter = true,
}: {
  feedbackKey: string;
  columnIndex: number;
  tableHeight: number;
  columnWidthStorage: number[];
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  className?: string;
  isTable?: boolean;
  canSort?: boolean;
  onChange?: (clauses: string[]) => void;
  runFacets?: RunFacetField[];
  currentFilters: string[];
  sessionId?: string;
  evaluatorFeedbackKeys?: string[];
  columnOverride?: ExperimentViewOverridesSchema['column_overrides'][number];
  statDisplay?: (stat: number) => ReactNode;
  canFilter?: boolean;
}) => {
  const isPlaygroundLoading = usePlaygroundStore(
    (state) => state.loadingState === 'loading'
  );
  const { session, expectedNumberOfRuns, sortBy, sortOrder, setSortParams } =
    useFeedbackColumnSorting({ sessionId, evaluatorFeedbackKeys });
  const sessionAvg = session?.feedback_stats?.[feedbackKey]?.avg;
  const sessionN = session?.feedback_stats?.[feedbackKey]?.n;
  const tooltipText = expectedNumberOfRuns
    ? `${sessionN} / ${expectedNumberOfRuns} evaluations completed`
    : undefined;

  return (
    <ExperimentColumnHeader
      key={feedbackKey}
      columnIndex={columnIndex}
      columnName={feedbackKey}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
      className={className}
      isTable={isTable}
    >
      <div className="flex flex-col">
        <div className="flex min-w-0 shrink-0 items-center gap-2 overflow-hidden break-words rounded-md">
          <TruncatedTextWithTooltip text={capitalize(feedbackKey)} />
        </div>
        {(canSort || onChange || sessionAvg != null) && (
          <div className="flex items-center justify-between gap-1">
            {sessionAvg != null && !isPlaygroundLoading && (
              <div className="mt-1.5 flex items-center">
                {statDisplay ? (
                  <Tooltip title={tooltipText}>
                    <div>{statDisplay(sessionAvg)}</div>
                  </Tooltip>
                ) : (
                  <AggregatedStatHeader
                    stat={sessionAvg}
                    type="AVG"
                    tooltip={tooltipText}
                    columnOverride={columnOverride}
                  />
                )}
              </div>
            )}
            <div className="flex gap-1">
              {canSort && (
                <IconButton
                  onClick={() => setSortParams(feedbackKey)}
                  icon={
                    sortBy === feedbackKey
                      ? sortOrder === 'ASC'
                        ? ArrowNarrowUpIcon
                        : ArrowNarrowDownIcon
                      : SwitchVertical02Icon
                  }
                  label={
                    sortBy === feedbackKey
                      ? `${feedbackKey} ${
                          sortOrder === 'ASC' ? 'ascending' : 'descending'
                        }`
                      : `Sort by ${feedbackKey}`
                  }
                  variant="outlined"
                  color={sortBy === feedbackKey ? 'primary' : 'secondary'}
                />
              )}
              {onChange && runFacets && canFilter && (
                <DatasetSessionCompareFilter
                  facets={runFacets.filter(
                    (facet) => facet.value === feedbackKey
                  )}
                  currentFilters={currentFilters}
                  onChange={onChange}
                  hideClearButton
                />
              )}
            </div>
          </div>
        )}
      </div>
    </ExperimentColumnHeader>
  );
};
